import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Button, 
  Alert, 
  Tabs, 
  Tab, 
  ListGroup,
  Badge,
  Modal,
  Form,
  Spinner
} from 'react-bootstrap';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import PaymentMethodForm from '../components/billing/PaymentMethodForm';
import billingService from '../services/billingService';
import paymentService from '../services/paymentService';
import featureFlagService, { FEATURES } from '../services/featureFlagService';

// Load stripe with the public key from environment
// For development without an env key, you can use Stripe's test key
const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLIC_KEY || 'pk_test_51OxeuRLN9lbv2IrLSpKEO5zojlerE4K9Jgy2PnVhYqbh4QOPnoqkwFxIXUgFz5W5diFSuvq6dmNezfez82KygqQD008PBQYe1A';
const stripePromise = loadStripe(STRIPE_PK);

// Stripe elements appearance
const appearance = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#0570de',
    colorBackground: '#ffffff',
    colorText: '#30313d',
  }
};

const BillingManagementPage = () => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);

  // Check feature flag and fetch billing data on component mount
  useEffect(() => {
    const checkFeatureFlag = () => {
      const isEnabled = featureFlagService.isFeatureEnabled(FEATURES.STRIPE_PAYMENTS);
      setPaymentsEnabled(isEnabled);
      
      // Only fetch billing data if payments are enabled
      if (isEnabled) {
        fetchBillingData();
      } else {
        setLoading(false); // Stop loading if feature is disabled
      }
    };
    
    // Check initially
    checkFeatureFlag();
    
    // Set up interval to check periodically in case flag changes
    const interval = setInterval(checkFeatureFlag, 30000); // Check every 30 seconds
    
    return () => clearInterval(interval);
  }, []);

  const fetchBillingData = async () => {
    setLoading(true);
    setError('');
    
    try {
      // In a real implementation, these would be separate API calls
      // For now, we'll mock this data
      
      // Mock payment methods data
      setPaymentMethods([
        { 
          id: 'pm_123456', 
          brand: 'visa', 
          last4: '4242', 
          expMonth: 12, 
          expYear: 2028, 
          isDefault: true 
        }
      ]);
      
      // Mock invoices data
      setInvoices([
        { 
          id: 'in_123456', 
          amount: 500000, // in cents
          status: 'paid', 
          date: new Date('2025-05-15').toISOString(),
          description: 'CRNA Negotiation Fee'
        }
      ]);
      
      // Mock subscription data
      setSubscription({
        id: 'sub_123456',
        status: 'active',
        currentPeriodEnd: new Date('2025-07-15').toISOString(),
        plan: 'Premium'
      });
      
    } catch (err) {
      console.error('Error fetching billing data:', err);
      setError('Failed to load billing information. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Prepare to add a payment method by creating a setup intent
  const prepareAddPaymentMethod = async () => {
    setProcessingPayment(true);
    try {
      // In a real implementation, we would call our backend to create a setup intent
      // and get a client secret
      // This is a mock implementation:
      
      // Simulate a delay for the API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock a client secret - in real app this would come from backend
      setClientSecret('seti_mock_client_secret_' + Date.now());
      setShowAddPaymentModal(true);
    } catch (err) {
      console.error('Error preparing payment method:', err);
      setError('Failed to initialize payment setup. Please try again.');
    } finally {
      setProcessingPayment(false);
    }
  };
  
  const handleAddPaymentMethod = async (paymentMethodData) => {
    setProcessingPayment(true);
    try {
      // This would call the backend to save the payment method
      console.log('Adding payment method:', paymentMethodData);
      
      // Mock successful addition
      const newPaymentMethod = {
        id: `pm_${Date.now()}`,
        brand: paymentMethodData.card.brand,
        last4: paymentMethodData.card.last4,
        expMonth: paymentMethodData.card.exp_month,
        expYear: paymentMethodData.card.exp_year,
        isDefault: paymentMethods.length === 0 // Make default if it's the first one
      };
      
      setPaymentMethods([...paymentMethods, newPaymentMethod]);
      setShowAddPaymentModal(false);
      
      // Reset the client secret
      setClientSecret('');
    } catch (err) {
      console.error('Error adding payment method:', err);
      setError('Failed to add payment method. Please try again.');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleSetDefaultPaymentMethod = (paymentMethodId) => {
    // Update default payment method
    const updatedPaymentMethods = paymentMethods.map(pm => ({
      ...pm,
      isDefault: pm.id === paymentMethodId
    }));
    
    setPaymentMethods(updatedPaymentMethods);
  };

  const handleRemovePaymentMethod = (paymentMethodId) => {
    // Remove payment method
    const updatedPaymentMethods = paymentMethods.filter(
      pm => pm.id !== paymentMethodId
    );
    
    setPaymentMethods(updatedPaymentMethods);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount / 100); // Convert cents to dollars
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Container className="my-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3">Loading billing information...</p>
      </Container>
    );
  }
  
  if (!paymentsEnabled) {
    return (
      <Container className="my-5">
        <h1 className="mb-4">Billing Management</h1>
        <Card className="text-center">
          <Card.Body>
            <Card.Title>Billing System Unavailable</Card.Title>
            <Card.Text>
              Our billing management system is currently undergoing maintenance and improvements.
              Please check back later or contact support for assistance with billing matters.
            </Card.Text>
            <Button variant="primary" href="/contact">
              Contact Support
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="my-5">
      <h1 className="mb-4">Billing Management</h1>
      
      {error && <Alert variant="danger">{error}</Alert>}
      
      <Tabs defaultActiveKey="payment-methods" id="billing-tabs" className="mb-4">
        <Tab eventKey="payment-methods" title="Payment Methods">
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h3>Your Payment Methods</h3>
                <Button 
                  variant="primary" 
                  onClick={prepareAddPaymentMethod}
                  disabled={processingPayment}
                >
                  {processingPayment ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                      <span className="ms-2">Loading...</span>
                    </>
                  ) : (
                    'Add Payment Method'
                  )}
                </Button>
              </div>
              
              {paymentMethods.length > 0 ? (
                <ListGroup>
                  {paymentMethods.map(method => (
                    <ListGroup.Item 
                      key={method.id}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <div>
                        <div className="fw-bold text-capitalize">
                          {method.brand} •••• {method.last4}
                          {method.isDefault && (
                            <Badge bg="success" className="ms-2">Default</Badge>
                          )}
                        </div>
                        <div className="text-muted">
                          Expires {method.expMonth}/{method.expYear}
                        </div>
                      </div>
                      <div>
                        {!method.isDefault && (
                          <Button 
                            variant="outline-primary" 
                            size="sm"
                            className="me-2"
                            onClick={() => handleSetDefaultPaymentMethod(method.id)}
                          >
                            Set as Default
                          </Button>
                        )}
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => handleRemovePaymentMethod(method.id)}
                          disabled={method.isDefault && paymentMethods.length > 1}
                        >
                          Remove
                        </Button>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              ) : (
                <Alert variant="info">
                  You haven't added any payment methods yet.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Tab>
        
        <Tab eventKey="invoices" title="Billing History">
          <Card>
            <Card.Body>
              <h3>Payment History</h3>
              
              {invoices.length > 0 ? (
                <ListGroup>
                  {invoices.map(invoice => (
                    <ListGroup.Item 
                      key={invoice.id}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <div>
                        <div className="fw-bold">{invoice.description}</div>
                        <div className="text-muted">{formatDate(invoice.date)}</div>
                      </div>
                      <div>
                        <span className="me-3 fw-bold">{formatCurrency(invoice.amount)}</span>
                        <Badge bg={invoice.status === 'paid' ? 'success' : 'warning'}>
                          {invoice.status === 'paid' ? 'Paid' : 'Pending'}
                        </Badge>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              ) : (
                <Alert variant="info">
                  You don't have any billing history yet.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Tab>
        
        <Tab eventKey="subscription" title="Subscription">
          <Card>
            <Card.Body>
              <h3>Your Subscription</h3>
              
              {subscription ? (
                <div>
                  <Row className="mb-3">
                    <Col md={6}>
                      <strong>Plan:</strong> {subscription.plan}
                    </Col>
                    <Col md={6}>
                      <strong>Status:</strong>{' '}
                      <Badge bg={subscription.status === 'active' ? 'success' : 'warning'}>
                        {subscription.status === 'active' ? 'Active' : 'Inactive'}
                      </Badge>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={12}>
                      <strong>Current Period End:</strong> {formatDate(subscription.currentPeriodEnd)}
                    </Col>
                  </Row>
                  
                  <div className="mt-4">
                    <Button variant="outline-danger">
                      Cancel Subscription
                    </Button>
                  </div>
                </div>
              ) : (
                <Alert variant="info">
                  You don't have an active subscription.
                  <div className="mt-3">
                    <Button variant="success" href="/upgrade">
                      Upgrade Now
                    </Button>
                  </div>
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
      
      {/* Add Payment Method Modal */}
      <Modal 
        show={showAddPaymentModal} 
        onHide={() => setShowAddPaymentModal(false)}
        backdrop="static"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Payment Method</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {clientSecret ? (
            <Elements
              stripe={stripePromise} 
              options={{
                appearance,
                clientSecret
              }}
            >  
              <PaymentMethodForm 
                onSubmit={handleAddPaymentMethod}
                processing={processingPayment}
              />
            </Elements>
          ) : (
            <div className="text-center p-4">
              <Spinner animation="border" />
              <p className="mt-3">Preparing payment form...</p>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default BillingManagementPage;
